import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getBrandIdentity, getWidgetKey } from '../../services/configService';

const CHAT_URL  = process.env.REACT_APP_CHAT_URL  || '';
const SESSION_KEY = 'pulse_session_id';

function genSessionId() {
  return `live_preview_${crypto.randomUUID?.() ?? (Date.now().toString(36) + Math.random().toString(36).slice(2))}`;
}

function buildSrcdoc({ brandColor, assistantName, welcomeMessage, darkMode, widgetKey, sessionId }) {
  const origin = window.location.origin;
  const theme = darkMode ? 'dark' : 'light';
  // JSON.stringify handles escaping safely for inline script injection
  const config = JSON.stringify({
    brandColor, assistantName, welcomeMessage, theme,
    apiUrl: CHAT_URL, apiKey: widgetKey,
  });
  return `<!DOCTYPE html><html style="margin:0;padding:0">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:${darkMode ? '#0f172a' : '#ffffff'};width:100%;height:100vh;">
<script>
  // Force lg-screen layout — the iframe is narrow but we don't want mobile mode
  Object.defineProperty(window, 'innerWidth',  { get: () => 1280, configurable: true });
  Object.defineProperty(window, 'innerHeight', { get: () => 900,  configurable: true });
  // Pin the preview session so the widget doesn't pick up a previous live session
  try { localStorage.setItem(${JSON.stringify(SESSION_KEY)}, ${JSON.stringify(sessionId)}); } catch(e) {}
  // Pin the theme so a previous in-widget toggle doesn't override the configured one
  try { localStorage.setItem('pulse_theme', ${JSON.stringify(theme)}); } catch(e) {}
  window.PulseWidgetConfig = ${config};
<\/script>
<script src="${origin}/pulse-widget-v2.js"><\/script>
<script>
  // Auto-open then pin the panel to fill the frame (no launcher, no close btn)
  var _t = setInterval(function() {
    if (!window.PulseWidget) return;
    PulseWidget.open();
    clearInterval(_t);

    // Give the panel iframe time to render its content
    setTimeout(function() {
      var frames = document.querySelectorAll('iframe');
      frames.forEach(function(f) {
        var doc = f.contentDocument;
        if (!doc) return;

        if (doc.querySelector('.close-btn')) {
          // This is the panel frame — fill the container & hide close btn
          var PIN = 'position:fixed;inset:0;width:100%;height:100%;border:none;border-radius:0;opacity:1;pointer-events:all;transform:none;z-index:2147483647;';
          f.style.cssText = PIN;
          // Widget's applyPanelSize() re-fires on resize (keyboard dismiss on Android)
          // and resets bottom:88px — re-pin every time
          window.addEventListener('resize', function() { f.style.cssText = PIN; });
          var s = doc.createElement('style');
          // Square the panel so its rounded corners don't reveal a sliver of the
          // layer behind it — the outer preview iframe supplies the rounded frame.
          s.textContent = '.close-btn{display:none!important}.panel{border-radius:0!important}';
          doc.head.appendChild(s);
          // Message links use target=_parent (right for the real embed) which would
          // navigate the preview frame itself — in preview, open them in a new tab.
          doc.addEventListener('click', function(e) {
            var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
            if (!a) return;
            e.preventDefault();
            window.open(a.href, '_blank', 'noopener,noreferrer');
          }, true);
        } else {
          // Launcher frame — remove entirely so it can't affect layout
          f.remove();
        }
      });
      // Signal parent that DOM manipulation is complete — parent unhides the iframe
      window.parent.postMessage({ type: 'pulse-preview-ready' }, '*');
    }, 300);
  }, 50);
<\/script>
</body></html>`;
}

const Spinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#475569', fontSize: 13, gap: 8 }}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
      style={{ animation: 'spin 0.8s linear infinite' }}>
      <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
    </svg>
    Loading preview…
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

export default function ChatPreview() {
  const { user }  = useAuth();
  const [cfg, setCfg] = useState(null);
  const [ready, setReady] = useState(false);
  const prevSession = useRef(null);

  // Fresh session ID every time cfg changes — preview always starts a clean chat
  const srcdoc = useMemo(() => cfg ? buildSrcdoc({ ...cfg, sessionId: genSessionId() }) : null, [cfg]);

  // Reset ready whenever srcdoc changes
  useEffect(() => { setReady(false); }, [srcdoc]);

  // Listen for the srcdoc signal that DOM manipulation is done
  useEffect(() => {
    function onMessage(e) {
      if (e.data?.type === 'pulse-preview-ready') setReady(true);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Save & restore the real widget's session so the preview doesn't clobber it
  useEffect(() => {
    try { prevSession.current = localStorage.getItem(SESSION_KEY); } catch {}
    return () => {
      try {
        if (prevSession.current == null) localStorage.removeItem(SESSION_KEY);
        else localStorage.setItem(SESSION_KEY, prevSession.current);
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    Promise.all([getBrandIdentity(), getWidgetKey()])
      .then(([identity, keyData]) => {
        const c = identity.config || {};
        setCfg({
          brandColor:     c.brandColor     || '#479EC3',
          assistantName:  c.assistantName  || 'AI Assistant',
          welcomeMessage: c.welcomeMessage || 'Hi! How can I help you today?',
          darkMode:       c.darkMode ?? false,
          widgetKey:      keyData.widgetKey || keyData.key || '',
        });
      })
      .catch(() => setCfg({ brandColor: '#479EC3', assistantName: 'AI Assistant', welcomeMessage: 'Hi! How can I help you today?', darkMode: false, widgetKey: '' }));
  }, [user]);

  if (!srcdoc) return <Spinner />;

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 8px', boxSizing: 'border-box', gap: 10 }}>
      {!ready && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          <Spinner />
        </div>
      )}
      <iframe
        srcDoc={srcdoc}
        style={{ width: 'min(560px, 96vw)', flex: '1 1 0', minHeight: 0, maxHeight: 820, border: 'none', borderRadius: 16,
          opacity: ready ? 1 : 0, transition: 'opacity 0.3s' }}
        title="Live Widget Preview"
      />
    </div>
  );
}
